/**
 * realtime — the page side of the framework's socket.io push (api.md
 * "Realtime — socket.io"): ONE socket per page on the API's own server,
 * authenticated by the session cookie, rooms the app declared in its
 * api/realtime.js, events pushed by realtime.emit(room, event, payload).
 *
 * Lifecycle (the whole contract, docs/libs/realtime.md):
 *   - boot: g.settings.realtime = { enabled, path } arrives from main.json
 *     through g.init (the SAME block the API reads); enabled + a signed-in
 *     user (g.settings.user) ⇒ the socket connects by itself. Anonymous or
 *     disabled ⇒ nothing is attempted: state().reason says why, join()
 *     answers { ok: false, reason }.
 *   - join(room) asks the server (the app's allow() decides), REMEMBERS
 *     the room and re-joins it on every reconnection — the server forgets
 *     a socket's rooms when the socket drops (a network gap, a server
 *     restart), the page must not notice. A re-join the server now refuses
 *     drops the room silently (the app's allow() changed its mind).
 *   - 'session-ended' (the session closed anywhere: logout, revoke, expiry)
 *     ENDS the lib for this page: no reconnection, state().ended = true,
 *     `realtime:ended` on document. A new sign-in is a new page load, which
 *     connects fresh — nothing to do here. A handshake refused with
 *     'unauthorized' while reconnecting means the same thing.
 *   - every server event reaches on(event, fn) listeners AND document as
 *     `realtime:event` { event, payload }; the connection itself reports as
 *     `realtime:connected` / `realtime:disconnected` { reason }.
 */

const DEFAULT_PATH = '/frm/socket.io';

const state = {
	socket: null,
	connected: false,
	ended: false,
	reason: null,        // why there is no socket: 'disabled' | 'anonymous' | 'no vendor' | 'ended' | null
	rooms: new Set(),    // the rooms the page asked for — re-joined on every (re)connection
	listeners: new Map(), // event → Set<fn>
	connecting: null,    // the in-flight connect() promise
};

const settings = () => (window.g && window.g.settings) || {};
const config = () => {
	const raw = settings().realtime;
	return raw && typeof raw === 'object' ? raw : { enabled: false, path: DEFAULT_PATH };
};

function fire(name, detail) {
	document.dispatchEvent(new CustomEvent('realtime:' + name, { detail: detail ?? {}, bubbles: false, cancelable: false }));
}

function deliver(event, payload) {
	const set = state.listeners.get(event);
	if (set) {
		for (const fn of [...set]) {
			try { fn(payload, event); } catch (err) { console.error('[realtime] listener for "' + event + '" threw', err); }
		}
	}
	fire('event', { event, payload });
}

/** Why the lib cannot (or must not) open a socket right now — null when it can. */
function blocker() {
	if (state.ended) return 'ended';
	if (config().enabled !== true) return 'disabled';
	if (!settings().user) return 'anonymous';
	if (typeof window.io !== 'function') return 'no vendor';
	return null;
}

function joinOnSocket(room) {
	return new Promise((resolve) => {
		let done = false;
		const timer = setTimeout(() => { if (!done) { done = true; resolve({ ok: false, reason: 'timeout' }); } }, 8000);
		state.socket.emit('join', room, (ack) => {
			if (done) return;
			done = true;
			clearTimeout(timer);
			resolve(ack && typeof ack === 'object' ? ack : { ok: false, reason: 'no ack' });
		});
	});
}

function leaveOnSocket(room) {
	return new Promise((resolve) => {
		let done = false;
		const timer = setTimeout(() => { if (!done) { done = true; resolve({ ok: false, reason: 'timeout' }); } }, 8000);
		state.socket.emit('leave', room, (ack) => {
			if (done) return;
			done = true;
			clearTimeout(timer);
			resolve(ack && typeof ack === 'object' ? ack : { ok: false, reason: 'no ack' });
		});
	});
}

/** After every (re)connection: the remembered rooms are joined again. */
async function rejoinAll() {
	const socket = state.socket;
	for (const room of [...state.rooms]) {
		if (state.socket !== socket || !state.connected) return;
		const ack = await joinOnSocket(room);
		if (ack.ok !== true && state.socket === socket) {
			// the app's allow() no longer agrees (or the room vanished) — the
			// page is told once, the room is forgotten
			state.rooms.delete(room);
			console.warn('[realtime] re-join of "' + room + '" refused: ' + (ack.reason ?? 'unknown'));
			fire('left', { room, reason: ack.reason ?? 'refused' });
		}
	}
}

function end(reason) {
	state.ended = true;
	state.reason = 'ended';
	const socket = state.socket;
	if (socket) {
		socket.io.opts.reconnection = false;
		socket.removeAllListeners();
		socket.io.removeAllListeners?.();
		socket.disconnect();
	}
	state.socket = null;
	const wasConnected = state.connected;
	state.connected = false;
	if (wasConnected) fire('disconnected', { reason: 'session-ended' });
	fire('ended', { reason });
}

/**
 * Open the socket (idempotent: an open or opening socket answers the same
 * promise). Resolves true once connected, false when nothing was attempted
 * (see blocker()) or the first handshake failed for good.
 */
function connect() {
	if (state.connected) return Promise.resolve(true);
	if (state.connecting) return state.connecting;
	const why = blocker();
	if (why) {
		state.reason = why;
		if (why === 'no vendor') console.error("[realtime] socket.io vendor not loaded on this page - add 'vendors' => ['socketio'] next to 'libs' => ['realtime'] in the page's route.php");
		return Promise.resolve(false);
	}
	state.reason = null;
	const cfg = config();
	const origin = String(settings().api || '').replace(/\/+$/, '') || window.location.origin;
	const socket = window.io(origin, {
		path: cfg.path || DEFAULT_PATH,
		transports: ['websocket'],
		withCredentials: true,     // the session cookie IS the handshake's identity
		reconnection: true,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 10000,
		timeout: 10000,
	});
	state.socket = socket;

	state.connecting = new Promise((resolve) => {
		let settled = false;
		const settle = (value) => { if (!settled) { settled = true; state.connecting = null; resolve(value); } };

		socket.on('connect', () => {
			if (state.socket !== socket) return;
			state.connected = true;
			state.reason = null;
			fire('connected', { reconnected: settled });
			settle(true);
			rejoinAll();
		});
		socket.on('disconnect', (reason) => {
			if (state.socket !== socket) return;
			state.connected = false;
			fire('disconnected', { reason });
			// the server closed it on purpose (session-ended arrives first,
			// end() already ran) — anything else socket.io retries by itself
		});
		socket.on('connect_error', (err) => {
			if (state.socket !== socket) return;
			const message = String(err?.message ?? err ?? '');
			if (/unauthorized/i.test(message)) {
				// the session is gone (a reconnection after the session ended,
				// or a cookie that expired between page load and handshake)
				end('unauthorized');
				settle(false);
				return;
			}
			// a network refusal: socket.io keeps retrying; the FIRST attempt
			// failing settles connect() false, the socket stays alive
			if (!settled) fire('disconnected', { reason: 'connect_error', message });
			settle(false);
		});
		socket.on('session-ended', () => {
			if (state.socket !== socket) return;
			end('session-ended');
			settle(false);
		});
		socket.onAny((event, payload) => {
			if (state.socket !== socket) return;
			if (event === 'session-ended') return;
			deliver(event, payload);
		});
	});
	return state.connecting;
}

/** Close the socket on purpose (the rooms stay remembered for a later connect()). */
function disconnect() {
	const socket = state.socket;
	if (!socket) return;
	socket.io.opts.reconnection = false;
	socket.removeAllListeners();
	socket.disconnect();
	state.socket = null;
	state.connecting = null;
	const wasConnected = state.connected;
	state.connected = false;
	state.reason = blocker() ?? 'closed';
	if (wasConnected) fire('disconnected', { reason: 'io client disconnect' });
}

/**
 * Ask for a room: the server's answer ({ ok } or { ok: false, reason }) is
 * the promise's value. The room is remembered on ok — re-joined after any
 * reconnection until leave(). Before the socket is up the call waits for
 * it; with no socket possible it answers the blocker as the reason.
 */
async function join(room) {
	if (typeof room !== 'string' || room === '') return { ok: false, reason: 'room must be a non-empty string' };
	if (!state.connected) {
		const up = await connect();
		if (!up) return { ok: false, reason: state.reason ?? 'not connected' };
	}
	const ack = await joinOnSocket(room);
	if (ack.ok === true) state.rooms.add(room);
	return ack;
}

/** Leave a room and forget it. */
async function leave(room) {
	state.rooms.delete(room);
	if (!state.connected) return { ok: true };
	return leaveOnSocket(room);
}

/** Listen for a server event (pushed to a room this page joined). Returns the unsubscribe fn. */
function on(event, fn) {
	if (typeof event !== 'string' || typeof fn !== 'function') throw new Error('realtime.on(event, fn): a string and a function');
	if (!state.listeners.has(event)) state.listeners.set(event, new Set());
	state.listeners.get(event).add(fn);
	return () => off(event, fn);
}

function off(event, fn) {
	const set = state.listeners.get(event);
	if (!set) return;
	if (fn) set.delete(fn); else set.clear();
	if (set.size === 0) state.listeners.delete(event);
}

function snapshot() {
	const cfg = config();
	return {
		enabled: cfg.enabled === true,
		path: cfg.path || DEFAULT_PATH,
		connected: state.connected,
		ended: state.ended,
		reason: state.connected ? null : (state.reason ?? blocker()),
		rooms: [...state.rooms],
	};
}

/** Boot: auto-connect when the page can (enabled + signed in + vendor present). */
function boot() {
	const start = () => { if (!blocker()) connect(); else state.reason = blocker(); };
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
	else start();
}

export const realtime = {
	connect,
	disconnect,
	join,
	leave,
	on,
	off,
	rooms: () => [...state.rooms],
	state: snapshot,
	/** The underlying socket.io Socket (null before connect) — an escape hatch, not part of the contract. */
	socket: () => state.socket,
};

export { boot };
